/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag Fragment
 */
import React, { type ReactNode } from 'react';

import { css, jsx } from '@compiled/react';
import capitalize from 'lodash/capitalize';

import { LinkIconButton } from '@atlaskit/button/new';
import LinkIcon from '@atlaskit/icon/core/link';
import Link from '@atlaskit/link';
import { token } from '@atlaskit/tokens';

import { Assignee as AssigneeComponent } from '../../../../../../../common/ui/jira-assignee';
import { getJiraEntityIcon } from '../../../../../../../common/ui/product-entity-icon';

export type SearchJiraIssueTableRowProps = {
	/** URL of the avatar icon for the project (custom icon) */
	iconUrl: string;
	/** The type of the raw Jira node (board, project, etc.) */
	type: string;
	/** The category of the Jira project or space */
	category: string;
	/** The project or space lead -- a user */
	lead: { name: string; picture: string } | null;
	/** The project key */
	projectKey: string;
	/** The project style -- this can be something like Product Discovery or Team Manage Software */
	projectStyle: string;
	/** The project type that looks similar to the project style property */
	projectType: string;
	/** The project URL */
	projectUrl: string;
	/** The enriched project title -- contains query highlight matching */
	title: ReactNode;
	/** Called when the user clicks anywhere on the row. */
	onClick?: () => void;
};

const styles = {
	tr: css({
		display: 'table-row',
		height: '40px',
		backgroundColor: token('elevation.surface'),
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
		borderBottomColor: token('color.border'),
		cursor: 'pointer',
	}),
	tdSpace: css({
		display: 'table-cell',
		verticalAlign: 'middle',
		paddingInlineStart: token('space.100'),
		paddingInlineEnd: token('space.100'),
		// there is a top-level style that has very high-specificty that targets
		// the first cell of any tables and removes padding so we need to override this
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
		paddingLeft: `${token('space.100')} !important`,
		overflow: 'hidden',
	}),
	tdSpaceInner: css({
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		gap: token('space.075'),
		overflow: 'hidden',
	}),
	tdFixed: css({
		display: 'table-cell',
		verticalAlign: 'middle',
		paddingInlineStart: token('space.100'),
		paddingInlineEnd: token('space.100'),
		overflow: 'hidden',
		boxSizing: 'border-box',
		borderInlineStartWidth: token('border.width'),
		borderInlineStartStyle: 'solid',
		borderInlineStartColor: token('color.border'),
		whiteSpace: 'nowrap',
	}),
	link: css({
		flexShrink: 0,
		color: token('color.link'),
		fontWeight: token('font.weight.regular'),
	}),
	iconWrapper: css({
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
	}),
	tdLast: css({
		// there is a top-level style that has very high-specificty that targets
		// the last cell of any tables and removes padding so we need to override this
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
		paddingRight: `${token('space.100')} !important`,
	}),
};

const makeProjectStyleOrTypeReadable = (styleOrType: string): string => {
	return styleOrType.split('_').map(capitalize).join(' ');
};

export const SearchJiraProjectTableRow = ({
	iconUrl,
	type,
	projectKey,
	title,
	projectUrl,
	projectStyle,
	projectType,
	lead,
	category,
	onClick,
}: SearchJiraIssueTableRowProps): React.JSX.Element => {
	const Icon = getJiraEntityIcon({
		iconUrl,
		type,
		isIssue: false,
		size: 'small',
	});

	return (
		<tr css={styles.tr} data-testid="search-jira-issue-table-row" onClick={onClick}>
			{/* Space column: icon + title */}
			<td css={styles.tdSpace}>
				<div css={styles.tdSpaceInner}>
					<span css={styles.iconWrapper}>{Icon}</span>
					{title && (
						<Link href={projectUrl} target="_blank" onClick={onClick}>
							{title}
						</Link>
					)}
				</div>
			</td>

			{/* Key column */}
			<td css={styles.tdFixed}>{projectKey || null}</td>

			{/* Type column */}
			<td css={styles.tdFixed}>{makeProjectStyleOrTypeReadable(projectStyle || projectType)}</td>

			{/* Lead column: user avatar and name */}
			<td css={styles.tdFixed}>
				<AssigneeComponent user={lead ?? null} />
			</td>

			{/* Category column */}
			<td css={styles.tdFixed}>{category}</td>

			{/* Space URL column: clickable link icon that takes the user to the project / space */}
			<td css={[styles.tdFixed, styles.tdLast]}>
				<LinkIconButton
					isTooltipDisabled={false}
					href={projectUrl}
					icon={LinkIcon}
					label={projectUrl}
				/>
			</td>
		</tr>
	);
};
